import { Attribute, Relationship, Resource } from 'json-api';
import { Translatable } from 'src/app/translation/translatable';
import { AtomResource } from '../classes/atom-resource';
import { WithPaymentDimensions } from '../common-behaviors/with-payment-dimensions.mixin';
import { BusinessUnit } from './business-unit.resource';
import { Catalog } from './catalog.resource';
import { ContentDimension } from './content-dimension.resource';
import { Currency } from './currency.resource';
import { OfferCapability } from './offer-capability.resource';
import { ResourceConfig } from 'src/app/resources/resources-interfaces/resource-config';
import {
  NumberFilter,
  StringFilter,
  ResourceListFilter,
} from 'src/app/filter/filter';
import { AtomService } from '../atom.service';
import { PaymentDimensionConfig } from './payment-dimension.resource';
import {
  BusinessunitConfig,
  CatalogConfig,
} from 'src/app/shared/tools/search/search-config';

const SKUMixinBase = WithPaymentDimensions(AtomResource);

@Resource({
  type: 'skus',
})
export class SKU extends SKUMixinBase {
  @Attribute() price?: number;
  @Attribute() units?: number;
  @Attribute() name?: Translatable;
  @Attribute({ key: 'external_id' }) externalId?: string;
  @Attribute() data?: Record<string, unknown>;

  @Relationship() businessunits?: BusinessUnit[];
  @Relationship() catalog?: Catalog;
  @Relationship() currency?: Currency;
  @Relationship() documents?: AtomResource[];
  @Relationship() offercapabilities?: OfferCapability[];
  @Relationship() contentdimensions?: ContentDimension[];
}
export class SKUConfig implements ResourceConfig {
  tables = {
    includes: ['catalog'],
    columns: [
      {
        columnDef: 'id',
        displayedDatas: [{ path: ['id'], class: 'main' }],
        header: 'ID',
        translatable: false,
        displayed: true,
        sort: 'id',
      },
      {
        columnDef: 'externalId',
        displayedDatas: [
          {
            path: ['externalId'],
            class: 'mat-body-2',
          },
        ],
        header: 'External ID (Operator Label)',
        displayed: true,
        link: {
          detail: true,
        },
      },
      {
        columnDef: 'name',
        displayedDatas: [
          {
            path: ['name'],
            class: 'mat-body-2',
          },
        ],
        header: 'Name',
        displayed: true,
        link: {
          detail: true,
        },
      },
      {
        columnDef: 'catalog',
        header: 'Catalog',
        displayed: true,
        displayedDatas: [
          {
            path: ['catalog.name'],
            class: 'mat-body-2',
          },
        ],
        link: {
          resource: 'catalogs',
          filter: 'catalog.id',
          icon: 'folder_open',
        },
        sort: 'catalog.name',
      },
      {
        columnDef: 'contracts',
        header: 'contracts',
        displayed: true,
        link: {
          resource: 'contracts',
          list: 'contracts',
          target: 'catalogs.skus',
        },
      },
      {
        columnDef: 'actions',
        header: 'Actions',
        displayed: true,
      },
    ],
  };
  detail = {
    header: {
      icon: 'qr_code',
      title: [
        {
          path: ['name'],
          translatable: false,
        },
      ],
      id: true,
    },
    elements: [
      {
        title: 'External ID',
        list: [
          {
            APIKey: 'externalId',
            value: {
              path: ['externalId'],
            },
            options: {
              copy: true,
            },
            inherit: {
              resourceKey: 'paymentdimensions',
              path: 'platform.externalIdName',
              title: 'Operator Label',
            },
          },
        ],
      },
      {
        columns: true,
        list: [
          {
            icon: 'folder_open',
            title: 'Catalog',
            APIKey: 'catalog',
            value: {
              path: ['catalog.name'],
            },
            link: {
              resource: 'catalogs',
              filter: 'catalog.id',
            },
          },
          {
            icon: 'edit',
            title: 'Contracts',
            APIKey: 'contracts',
            link: {
              resource: 'contracts',
              list: 'sku.catalogs',
              target: 'catalogs.skus',
            },
          },
          {
            icon: 'inventory_2',
            title: 'Units',
            APIKey: 'units',
            value: {
              path: ['units'],
              // addUnit: {
              //   path: 'currency.id',
              // },
            },
          },
          {
            icon: 'attach_money',
            title: 'Price',
            APIKey: 'price',
            value: {
              path: ['price'],
              addUnit: {
                path: 'currency.id',
              },
            },
          },
        ],
      },
      {
        title: 'Business Units',
        list: [
          {
            type: 'chips',
            APIKey: 'businessunits',
            value: {
              path: ['name'],
            },
            inherit: {
              resourceKey: 'catalog',
              path: 'name',
              from: 'catalog',
            },
          },
        ],
      },
    ],
    tabs: [
      {
        title: 'Payment dimensions',
        elements: [
          {
            resource: {
              APIKey: 'paymentdimensions',
              config: new PaymentDimensionConfig(this.atom, 'sku', true),
              multiple: true,
            },
          },
        ],
      },
      {
        title: 'Custom Fields',
        elements: [
          {
            list: [
              {
                type: 'customFields',
                APIKey: 'paymentdimensions',
              },
            ],
          },
        ],
      },
    ],
  };
  wizard = {
    tabs: [
      {
        type: 'resource',
        title: 'General',
        formGroup: 'general',
        list: [
          {
            type: 'text',
            title: 'Name',
            formControl: 'name',
            APIKey: 'name',
            required: true,
            translatable: true,
            noCols: true,
          },
          {
            type: 'resource',
            title: 'Catalog',
            formControl: 'catalog',
            APIKey: 'catalog',
            search: new CatalogConfig(this.atom),
            multiple: false,
            required: true,
            noCols: true,
            activatingSequence: [
              {
                group: 'general',
                control: 'externalId',
                noNullIfInactivated: true,
                type: 'string',
                inherit: true,
              },
              {
                group: 'customfields',
                control: 'customfields',
                type: 'resource',
                host: true,
              },
              {
                group: 'general',
                control: 'businessunits',
                type: 'resource',
                inherit: true,
              },
            ],
          },
          {
            type: 'text',
            title: 'External ID',
            formControl: 'externalId',
            APIKey: 'externalId',
            required: true,
            noCols: true,
            inherit: {
              resourceKey: 'paymentdimensions',
              path: 'platform.externalIdName',
              title: 'Operator Label',
            },
          },
          {
            type: 'resource',
            title: 'Business Units',
            formControl: 'businessunits',
            APIKey: 'businessunits',
            search: new BusinessunitConfig(true),
            multiple: true,
            required: false,
            noCols: true,
            inherit: {
              resourceKey: 'businessunits',
              path: 'name',
            },
          },
        ],
      },
      {
        title: 'Custom Fields',
        formGroup: 'customfields',
        list: [
          {
            type: 'customfields',
            formControl: 'data',
            required: false,
            noCols: true,
          },
        ],
      },
    ],
  };
  resource = SKU;
  resourceName?: string | undefined = 'skus';
  resourceKey?: string | undefined = 'skus';
  resourceKeySingle?: string | undefined = 'sku';
  includes = [
    'catalog.businessunits',
    'businessunits',
    'paymentdimensions.platform.customfields',
    'currency',
  ];
  filters = [
    new NumberFilter('id', 'ID'),
    new StringFilter('name', 'Name'),
    new StringFilter('external_id', 'External ID', true),
    new ResourceListFilter(
      this.atom,
      BusinessUnit,
      'businessunits.id',
      'Business unit'
    ),
    new StringFilter(
      'catalog.contracts.paymentdimensions.billingproviderjob.firm.name',
      'Billing Provider'
    ),
    new StringFilter(
      'catalog.contracts.paymentdimensions.payoutproviderjob.firm.name',
      'Payout Provider'
    ),
    new StringFilter(
      'catalog.contracts.paymentdimensions.enduserproviderjob.firm.name',
      'End User Provider'
    ),
    new StringFilter(
      'catalog.contracts.paymentdimensions.country.id',
      'Country ID'
    ),
    new StringFilter(
      'catalog.contracts.paymentdimensions.country.name',
      'Country name'
    ),
    new NumberFilter(
      'catalog.contracts.paymentdimensions.platform.id',
      'Platform ID'
    ),
    new StringFilter(
      'catalog.contracts.paymentdimensions.platform.name',
      'Platform name'
    ),
    new StringFilter(
      'catalog.contracts.paymentdimensions.cascade.name',
      'Cascade name'
    ),
    new NumberFilter('catalog.id', 'Catalog ID'),
    new StringFilter('catalog.name', 'Catalog name'),
    new NumberFilter('catalog.contracts.id', 'Contract ID'),
    new StringFilter('catalog.contracts.name', 'Contract name'),
  ];
  constructor(private atom: AtomService) {}
}
